// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import TerceraDateRangeDropDownFormTemplate from '@front/templates/TerceraDateRangeDropDownFormTemplate.html';
import { Resources } from '@shared/localizations/Resources';
import { TerceraDateDropDownBase } from './TerceraDateDropDownBase';
import { BOTTOM_WINDOWS_MARGIN } from '../../UtilsClasses/SizeConstants';
import { ControlEvents } from '../Control';
import { ControlsTypes } from '../../UtilsClasses/FactoryConstants';
export class TerceraDateRangeDropDownForm extends TerceraDateDropDownBase {
    constructor() {
        super(...arguments);
        this._dateFrom = null;
        this._dateTo = null;
    }
    getType() {
        return ControlsTypes.TerceraDateRangeDropDownForm;
    }
    oncomplete() {
        super.oncomplete();
        const pickerFrom = this.getPickerFrom();
        const pickerTo = this.getPickerTo();
        if (isNullOrUndefined(pickerFrom) || isNullOrUndefined(pickerTo))
            return;
        const modelFrom = this.get('dateFrom');
        const modelTo = this.get('dateTo');
        this._dateFrom = isNullOrUndefined(this._dateFrom) ? modelFrom : this._dateFrom;
        this._dateTo = isNullOrUndefined(this._dateTo) ? modelTo : this._dateTo;
        if (!isNullOrUndefined(this._dateFrom))
            pickerFrom.selectDate(this._dateFrom);
        if (!isNullOrUndefined(this._dateTo))
            pickerTo.selectDate(this._dateTo);
        this.on('applyBtnClick', this.close);
        pickerFrom.update('onRenderCell', this.datePickerOnRenderCell.bind(this));
        pickerTo.update('onRenderCell', this.datePickerOnRenderCell.bind(this));
        pickerFrom.update('onSelect', this.onSelectFrom.bind(this));
        pickerTo.update('onSelect', this.onSelectTo.bind(this));
        this.applyConstraints();
        this.on(ControlEvents.LostFocus, () => {
            this.close();
        });
        this.setFocus();
    }
    setDateFrom(date) {
        this._dateFrom = date;
        void this.set({ dateFrom: date });
        this.applyConstraints();
        const pickerFrom = this.getPickerFrom();
        if (!isNullOrUndefined(pickerFrom)) {
            this.selectIfDifferent(pickerFrom, date);
        }
    }
    setDateTo(date) {
        this._dateTo = date;
        void this.set({ dateTo: date });
        this.applyConstraints();
        const pickerTo = this.getPickerTo();
        if (!isNullOrUndefined(pickerTo)) {
            this.selectIfDifferent(pickerTo, date);
        }
    }
    datePickerOnRenderCell(date, cellType) {
        if (cellType !== 'day')
            return null;
        const from = this._dateFrom;
        const to = this._dateTo;
        if (!isNullOrUndefined(from) && !isNullOrUndefined(to) && date >= from && date <= to) {
            return this.getRangeClassesIfInRange(date, from, to);
        }
        return null;
    }
    setCorrectLocation(x, y) {
        const width = this.get('width');
        const height = this.get('height');
        if (x + width > window.innerWidth) {
            x = window.innerWidth - width - 2;
        }
        if (y + height > window.innerHeight - BOTTOM_WINDOWS_MARGIN) {
            y = window.innerHeight - BOTTOM_WINDOWS_MARGIN - height - 2;
        }
        void this.set({ left: x, top: y });
    }
    onSelectFrom(_formatted, date) {
        const pickerFrom = this.getPickerFrom();
        const pickerTo = this.getPickerTo();
        if (isNullOrUndefined(pickerFrom) || isNullOrUndefined(pickerTo))
            return;
        const from = this.extractDate(pickerFrom, date);
        let to = this._dateTo;
        if (!isNullOrUndefined(from) && (isNullOrUndefined(to) || from > to)) {
            to = from;
        }
        this.setRange(from, to);
        if (!isNullOrUndefined(to) && !isNullOrUndefined(from) && from.isSameDay(to)) {
            this.selectIfDifferent(pickerTo, to);
        }
    }
    onSelectTo(_formatted, date) {
        const pickerFrom = this.getPickerFrom();
        const pickerTo = this.getPickerTo();
        if (isNullOrUndefined(pickerFrom) || isNullOrUndefined(pickerTo))
            return;
        const to = this.extractDate(pickerTo, date);
        let from = this._dateFrom;
        if (!isNullOrUndefined(to) && (isNullOrUndefined(from) || to < from)) {
            from = to;
        }
        this.setRange(from, to);
        if (!isNullOrUndefined(from) && !isNullOrUndefined(to) && from.isSameDay(to)) {
            this.selectIfDifferent(pickerFrom, from);
        }
    }
    getPickerFrom() {
        const c = this.Controls.dtPickerFrom;
        if (isNullOrUndefined(c))
            return null;
        return c.picker;
    }
    getPickerTo() {
        const c = this.Controls.dtPickerTo;
        if (isNullOrUndefined(c))
            return null;
        return c.picker;
    }
    getSelectedDate(picker) {
        const arr = picker.selectedDates;
        if (Array.isArray(arr) && arr.length > 0 && arr[0] instanceof Date) {
            return arr[0];
        }
        return null;
    }
    extractDate(picker, dateArg) {
        if (dateArg instanceof Date)
            return dateArg;
        if (Array.isArray(dateArg) && dateArg.length > 0) {
            const d0 = dateArg[0];
            if (d0 instanceof Date)
                return d0;
        }
        const selected = this.getSelectedDate(picker);
        if (!isNullOrUndefined(selected))
            return selected;
        const current = picker.currentDate;
        return current instanceof Date ? current : null;
    }
    selectIfDifferent(picker, date) {
        const selected = this.getSelectedDate(picker);
        if (isNullOrUndefined(selected) || !selected.isSameDay(date)) {
            picker.selectDate(date);
        }
    }
    applyConstraints() {
        const pickerFrom = this.getPickerFrom();
        const pickerTo = this.getPickerTo();
        if (isNullOrUndefined(pickerFrom) || isNullOrUndefined(pickerTo))
            return;
        pickerTo.update('minDate', isNullOrUndefined(this._dateFrom) ? null : this._dateFrom);
        pickerFrom.update('maxDate', isNullOrUndefined(this._dateTo) ? null : this._dateTo);
    }
    setRange(from, to) {
        this._dateFrom = from;
        this._dateTo = to;
        void this.set({ dateFrom: from, dateTo: to });
        this.applyConstraints();
        const pickerFrom = this.getPickerFrom();
        const pickerTo = this.getPickerTo();
        if (!isNullOrUndefined(pickerFrom) && !isNullOrUndefined(from)) {
            this.selectIfDifferent(pickerFrom, from);
        }
        if (!isNullOrUndefined(pickerTo) && !isNullOrUndefined(to)) {
            this.selectIfDifferent(pickerTo, to);
        }
    }
}
TerceraDateDropDownBase.extendWith(TerceraDateRangeDropDownForm, {
    data: () => ({
        width: 512,
        height: 392,
        resizable: false,
        showHeader: false,
        showFooter: false,
        dateFrom: null,
        dateTo: null,
        applyBtnText: Resources.getResource('screen.MonthCalendarPopupForm.Apply'),
        labelTextFrom: Resources.getResource('reports.fromDate'),
        labelTextTo: Resources.getResource('reports.toDate')
    }),
    partials: { bodyPartial: TerceraDateRangeDropDownFormTemplate }
});
